import { useEffect, useState } from 'react';
import type { ChangeEvent, FocusEvent, KeyboardEvent } from 'react';

export type ConfigItem = { code: string; mask: string; maxLength: number; flag: string };

export type Config = Record<string, ConfigItem>;

export const COUNTRY_CONFIGS: Config = {
	'380': { code: '+380', mask: '+380 (__) ___ __ __', maxLength: 13, flag: '🇺🇦' },
	'48': { code: '+48', mask: '+48 ___ ___ ___', maxLength: 13, flag: '🇵🇱' },
	'49': { code: '+49', mask: '+49 ___ ___ ____', maxLength: 14, flag: '🇩🇪' },
	'33': { code: '+33', mask: '+33 _ __ __ __ __', maxLength: 14, flag: '🇫🇷' },
	'39': { code: '+39', mask: '+39 ___ ___ ____', maxLength: 14, flag: '🇮🇹' },
	'34': { code: '+34', mask: '+34 ___ ___ ___', maxLength: 13, flag: '🇪🇸' },
	'44': { code: '+44', mask: '+44 ____ ______', maxLength: 14, flag: '🇬🇧' },
	'31': { code: '+31', mask: '+31 _ ________', maxLength: 13, flag: '🇳🇱' },
	'32': { code: '+32', mask: '+32 ___ __ __ __', maxLength: 15, flag: '🇧🇪' },
	'46': { code: '+46', mask: '+46 __ ___ ____', maxLength: 14, flag: '🇸🇪' },
	'43': { code: '+43', mask: '+43 ___ _________', maxLength: 16, flag: '🇦🇹' },
	'420': { code: '+420', mask: '+420 ___ ___ ___', maxLength: 14, flag: '🇨🇿' },
	'40': { code: '+40', mask: '+40 ___ ___ ___', maxLength: 13, flag: '🇷🇴' },
	'45': { code: '+45', mask: '+45 __ __ __ __', maxLength: 13, flag: '🇩🇰' },
	'47': { code: '+47', mask: '+47 ___ __ ___', maxLength: 12, flag: '🇳🇴' },
	'41': { code: '+41', mask: '+41 __ ___ __ __', maxLength: 14, flag: '🇨🇭' },
	'351': { code: '+351', mask: '+351 ___ ___ ___', maxLength: 14, flag: '🇵🇹' },
	'36': { code: '+36', mask: '+36 __ ___ ____', maxLength: 13, flag: '🇭🇺' },
	'30': { code: '+30', mask: '+30 ___ ___ ____', maxLength: 14, flag: '🇬🇷' },
};

export function usePhoneInputMask() {
	const [displayValue, setDisplayValue] = useState('');
	const [rawValue, setRawValue] = useState('');
	const [countryCode, setCountryCode] = useState<string>('');
	const [cursorPosition, setCursorPosition] = useState(0);

	const detectCountry = (digits: string) => {
		if (!digits) return null;
		const sortedCodes = Object.keys(COUNTRY_CONFIGS).sort((a, b) => b.length - a.length);

		for (const code of sortedCodes) {
			if (digits.startsWith(code)) {
				return COUNTRY_CONFIGS[code];
			}
		}
		return null;
	};

	const formatWithMask = (digits: string, config: ConfigItem | null) => {
		if (!config) {
			return digits ? `+${digits}` : '+';
		}

		const code = config?.code?.replace('+', '');
		const numberDigits = digits?.slice(code.length);

		let formatted = config?.code + ' ';
		let digitIndex = 0;
		const maskIndex = config?.code?.length + 1;

		for (let i = 0; i < config?.mask?.length - maskIndex; i++) {
			const char = config?.mask[maskIndex + i];

			if (char === '_') {
				if (digitIndex < numberDigits.length) {
					formatted += numberDigits[digitIndex];
					digitIndex++;
				} else {
					formatted += '_';
				}
			} else {
				formatted += char;
			}
		}

		return formatted;
	};

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		const input = e.target.value;
		const selectionStart = e.target.selectionStart || 0;

		let digitsOnly = input.replace(/\D/g, '');

		const config = detectCountry(digitsOnly);
		if (config) {
			const maxDigitsAllowed =
				(config.mask.match(/_/g) || []).length + config.code.replace(/\D/g, '').length;
			digitsOnly = digitsOnly.slice(0, maxDigitsAllowed);
		}

		setRawValue(digitsOnly);

		const detectedConfig = detectCountry(digitsOnly);
		setCountryCode(detectedConfig?.code || '');

		const formatted = formatWithMask(digitsOnly, detectedConfig);
		setDisplayValue(formatted);

		let newCursorPos = selectionStart;

		if (input.length > displayValue.length) {
			const beforeCursor = formatted.slice(0, selectionStart);
			const digitsBeforeCursor = beforeCursor.replace(/\D/g, '').length;

			newCursorPos = 0;
			let digitCount = 0;

			for (let i = 0; i < formatted.length; i++) {
				if (formatted[i].match(/\d/)) {
					digitCount++;
					if (digitCount >= digitsBeforeCursor) {
						newCursorPos = i + 1;
						break;
					}
				}
				if (digitCount < digitsBeforeCursor) {
					newCursorPos = i + 1;
				}
			}

			while (newCursorPos < formatted.length && !formatted[newCursorPos].match(/[\d_]/)) {
				newCursorPos++;
			}
		}

		setCursorPosition(newCursorPos);
	};

	const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
		if (!rawValue) {
			setDisplayValue('+');
			setTimeout(() => {
				e.target.setSelectionRange(1, 1);
			}, 0);
		}
	};

	const handleBlur = () => {
		if (rawValue.length === 0) {
			setDisplayValue('');
		}
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		const input = e.target as any;
		const start = input?.selectionStart || 0;

		if (e.key === 'Backspace') {
			if (start <= 1) {
				e.preventDefault();
				return;
			}

			const charBefore = displayValue[start - 1];
			if (charBefore && !charBefore.match(/\d/)) {
				setTimeout(() => {
					let newPos = start - 1;
					while (newPos > 0 && !displayValue[newPos - 1].match(/\d/)) {
						newPos--;
					}
					input.setSelectionRange(newPos, newPos);
				}, 0);
			}
		}

		if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
			setTimeout(() => {
				const newPos = input.selectionStart || 0;
				const char = displayValue[newPos];

				if (e.key === 'ArrowRight' && char && !char.match(/[\d_]/)) {
					input.setSelectionRange(newPos + 1, newPos + 1);
				}
				if (e.key === 'ArrowLeft' && newPos > 0 && !displayValue[newPos - 1].match(/[\d_]/)) {
					input.setSelectionRange(newPos - 1, newPos - 1);
				}
			}, 0);
		}
	};

	useEffect(() => {
		const input = document.activeElement as any;
		if (input && input?.tagName === 'INPUT' && cursorPosition > 0) {
			input?.setSelectionRange(cursorPosition, cursorPosition);
		}
	}, [displayValue, cursorPosition]);

	const clear = () => {
		setDisplayValue('');
		setRawValue('');
		setCountryCode('');
	};

	const getLocalNumber = () => {
		if (!countryCode || !rawValue) return '';
		const codeDigits = countryCode.replace('+', '');
		return rawValue.slice(codeDigits.length);
	};

	return {
		value: displayValue,
		rawValue,
		countryCode,
		localNumber: getLocalNumber(),
		onChange: handleChange,
		onFocus: handleFocus,
		onBlur: handleBlur,
		onKeyDown: handleKeyDown,
		clear,
	};
}
